import {Component, effect, inject, OnDestroy, OnInit, Signal, signal, ViewChild, ViewContainerRef} from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {debounceTime, distinctUntilChanged, map, shareReplay} from 'rxjs/operators';
import {NewAppointmentComponent, NewAppointmentDialogData} from '../new-appointment/new-appointment.component';
import {AppointmentType, Appointment, CalendarAppointment, calendarAppointmentToWriteModel, Events, Patient, QueryPatient, WebSocketService} from '@xmedical/core';
import {DayTimeSlot, DayViewComponent} from '../day-view/day-view.component';
import {MatDialog} from '@angular/material/dialog';
import {AppointmentDetailDialogComponent, AppointmentDetailDialogData} from '../appointment-detail-dialog/appointment-detail-dialog.component';
import {AppointmentMoveRequest} from '../fullcalendar-view/fullcalendar-view.component';
import {ConfirmationDialogComponent} from '@xmedical/ui';
import {EditAppointmentComponent} from '../edit-appointment/edit-appointment.component';
import {FormArray, FormBuilder, ReactiveFormsModule, UntypedFormGroup} from '@angular/forms';
import {CalendarStore} from '../../stores/calendar.store';
import {CALENDAR_ACTION_HANDLER, CALENDAR_DATA_PROVIDER, CALENDAR_HAS_TABS} from '../../tokens/calendar.tokens';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatButtonModule, MatFabButton} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatCardModule} from '@angular/material/card';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatMenuModule} from '@angular/material/menu';
import {AsyncPipe, DatePipe} from '@angular/common';
import {XmedicalUiModule} from '@xmedical/ui';
import {FullcalendarViewComponent} from '../fullcalendar-view/fullcalendar-view.component';
import dayjs from 'dayjs/esm';
import {PracticeLocation, PracticePractitioner} from '@xmedical/core';

type Dayjs = dayjs.Dayjs;

type NavigatorStep = 'day' | 'week' | 'month';

const START_HOUR = 7;
const END_HOUR = 18;
const TIMESLOT_MINS = 15;

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  providers: [CalendarStore],
  imports: [
    ReactiveFormsModule,
    MatToolbarModule,
    MatButtonModule,
    MatFabButton,
    MatIconModule,
    MatSidenavModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatSelectModule,
    MatExpansionModule,
    MatCheckboxModule,
    MatCardModule,
    MatButtonToggleModule,
    MatMenuModule,
    AsyncPipe,
    DatePipe,
    XmedicalUiModule,
    DayViewComponent,
    FullcalendarViewComponent,
  ]
})
export class CalendarComponent implements OnInit, OnDestroy {
  currentDate: Date;
  view: string;
  intervals: Date[] = [];
  visibleAppointmentTypes: number[] = [];
  appointmentTypes: AppointmentType[] = [];
  selectedAppointments: CalendarAppointment[] = [];
  selectedRow: DayTimeSlot = null;
  locations: PracticeLocation[] = [];
  practitioners: PracticePractitioner[] = [];
  selectedLocationId: number | null = null;
  selectedPractitionerId: number | null = null;
  locationsDisabled = false;
  practitionersDisabled = false;
  readonly initialized = signal(false);

  // Data for presentational DayView
  readonly dayViewAppointments = signal<CalendarAppointment[]>([]);

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset)
    .pipe(
      map(result => result.matches),
      shareReplay()
    );
  isExtraSmall = false;
  isExtraSmall$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.XSmall)
    .pipe(
      map(result => result.matches),
      shareReplay()
    );
  calendarForm: UntypedFormGroup;

  get hasTabs(): boolean {
    return this.hasTabsSignal?.() ?? false;
  }

  get fixedTopGap(): number {
    if (this.hasTabs && this.isExtraSmall) {
      return 160;
    }
    if (this.hasTabs && !this.isExtraSmall) {
      return 172;
    }
    if (!this.hasTabs && this.isExtraSmall) {
      return 112;
    }
    if (!this.hasTabs && !this.isExtraSmall) {
      return 128;
    }
    return 0;
  }

  private formEventsSubscription: Subscription;
  private routeParamsSubscription: Subscription;
  private handsetSizeSubscription: Subscription;
  private dayViewSubscription: Subscription;

  // WebSocket management (centralized for both views)
  private subscribedChannelNames: string[] = [];
  private wsCallback: ((data: unknown) => void) | null = null;

  // Cached FullCalendar date range for filter reloads
  private fullCalendarStartDate = '';
  private fullCalendarEndDate = '';

  @ViewChild(DayViewComponent) dayView: DayViewComponent;
  @ViewChild(FullcalendarViewComponent) fullCalendarView: FullcalendarViewComponent;

  private dataProvider = inject(CALENDAR_DATA_PROVIDER);
  private actionHandler = inject(CALENDAR_ACTION_HANDLER);
  readonly calendarStore = inject(CalendarStore);
  private pushService = inject(WebSocketService);
  private vcr = inject(ViewContainerRef);
  private hasTabsSignal: Signal<boolean> | null = inject(CALENDAR_HAS_TABS, {optional: true});

  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    public dialog: MatDialog,
  ) {
    effect(() => {
      if (this.dataProvider.isReady() && !this.initialized()) {
        this.initializeData();
      }
    });
  }

  get appointmentTypesFormArray(): FormArray {
    return this.calendarForm?.get('appointmentTypes') as FormArray ?? new FormArray([]);
  }

  get loaded(): boolean {
    return this.dataProvider.isReady() && !!this.calendarForm;
  }

  get navigatorStep(): NavigatorStep {
    return this.view === 'week' || this.view === 'month' ? this.view : 'day';
  }

  get patientDisplayFormat() {
    return this.dataProvider.practiceSettings()?.defaultPatientDisplayFormat ?? null;
  }

  get allowDragCreate(): boolean {
    return this.dataProvider.userSettings?.()?.allowDragCreate ?? true;
  }

  get allowTapCreate(): boolean {
    return this.dataProvider.userSettings?.()?.allowTapCreate ?? true;
  }

  get allowDragEditing(): boolean {
    return this.dataProvider.userSettings?.()?.allowDragEditing ?? false;
  }

  get allowFabCreate(): boolean {
    return this.dataProvider.userSettings?.()?.allowFabCreate ?? false;
  }

  get viewLabel(): string {
    switch (this.view) {
      case 'daysheet': return 'Daysheet';
      case 'day': return 'Day';
      case 'week': return 'Week';
      case 'month': return 'Month';
      default: return '';
    }
  }

  get appointmentsSelected(): boolean {
    return this.selectedAppointments.length > 0;
  }

  ngOnInit(): void {
    this.routeParamsSubscription = this.activatedRoute.paramMap.subscribe(params => {
      const view = params.get('view');
      const year = parseInt(params.get('year'), 10);
      const monthIndex = parseInt(params.get('month'), 10) - 1;
      const day = parseInt(params.get('day'), 10);
      this.currentDate = new Date(year, monthIndex, day);
      this.view = view;
      this.setupIntervals();
      if (this.initialized() && this.view === 'daysheet') {
        this.loadDayViewAppointments();
      }
    });
    this.handsetSizeSubscription = this.isExtraSmall$.subscribe(result => this.isExtraSmall = result);
  }

  private initializeData(): void {
    this.initialized.set(true);

    this.appointmentTypes = this.dataProvider.appointmentTypes().sort((a, b) => a.id > b.id ? 1 : -1) ?? [];
    this.visibleAppointmentTypes = this.dataProvider.defaultVisibleAppointmentTypeIds();

    this.locations = this.dataProvider.practiceLocations().filter(l => !l.isDeleted);
    this.locationsDisabled = this.locations.length <= 1;
    const defaultLocation = this.locations.find(l => l.isDefault) ?? this.locations[0];
    this.selectedLocationId = defaultLocation?.id ?? null;

    this.practitioners = this.dataProvider.practicePractitioners().filter(p => !p.isDeleted);
    this.practitionersDisabled = this.practitioners.length <= 1;
    const linkedId = this.dataProvider.linkedPracticePractitionerId?.();
    const linkedPractitioner = linkedId ? this.practitioners.find(p => p.id === linkedId) : null;
    const primaryPractitioner = linkedPractitioner ?? this.practitioners.find(p => p.isPrimary) ?? this.practitioners[0];
    this.selectedPractitionerId = primaryPractitioner?.id ?? null;

    this.calendarForm = this.formBuilder.group({
      appointmentTypes: this.formBuilder.array([])
    });
    this.appointmentTypes.forEach(type => {
      const isSelected = this.dataProvider.defaultVisibleAppointmentTypeIds().includes(type.id);
      const control = this.formBuilder.group({
        id: [type.id],
        selected: [isSelected]
      });
      this.appointmentTypesFormArray.push(control);
    });
    this.formEventsSubscription = this.calendarForm.valueChanges.pipe(debounceTime(750), distinctUntilChanged()).subscribe(value => {
      this.visibleAppointmentTypes = value.appointmentTypes.filter((type: { id: number, selected: boolean }) => type.selected).map((type: { id: number, selected: boolean }) => type.id);
    });

    this.setupWebSocket();

    if (this.currentDate && this.view === 'daysheet') {
      this.loadDayViewAppointments();
    } else if (this.fullCalendarStartDate && this.fullCalendarEndDate) {
      this.calendarStore.loadAppointments(
        this.fullCalendarStartDate,
        this.fullCalendarEndDate,
        this.selectedLocationId,
        this.selectedPractitionerId
      );
    }
  }

  ngOnDestroy(): void {
    this.routeParamsSubscription?.unsubscribe();
    this.handsetSizeSubscription?.unsubscribe();
    this.formEventsSubscription?.unsubscribe();
    this.dayViewSubscription?.unsubscribe();
    this.teardownWebSocket();
  }

  onAppointmentSelectionChanged(selectedAppointments: CalendarAppointment[]): void {
    this.selectedAppointments = selectedAppointments;
  }

  onRowSelectionChanged(selectedRow: DayTimeSlot): void {
    this.selectedRow = selectedRow;
  }

  dateChanged(date: Dayjs) {
    const month = date.month() + 1;
    const day = date.date();
    const year = date.year();
    const url = this.actionHandler.buildCalendarRoute(this.view, year, month, day);
    this.router.navigateByUrl(url);
  }

  selectToday() {
    this.dateChanged(dayjs());
  }

  sendBulkMessage() {
    this.actionHandler.onSendBulkMessageRequested?.(this.selectedAppointments.map(a => a.patient));
  }

  sendBulkEmail() {
    this.actionHandler.onSendBulkEmailRequested?.(this.selectedAppointments.map(a => a.patient));
  }

  addAppointmentFromFab() {
    const now = new Date();
    const defaultDuration = this.dataProvider.practiceSettings()?.defaultAppointmentDurationMinutes ?? 30;
    const end = new Date(now.getTime() + defaultDuration * 60 * 1000);
    this.onHelperEventClicked({start: now, end});
  }

  addNewAppointment() {
    const selectedTimeslot = this.selectedRow.timeslot;
    const defaultDuration = this.dataProvider.practiceSettings()?.defaultAppointmentDurationMinutes ?? 30;
    const endDate = new Date(selectedTimeslot.getTime() + defaultDuration * 60 * 1000);
    const dialogRef = this.dialog.open(NewAppointmentComponent, {
      disableClose: true,
      viewContainerRef: this.vcr,
      panelClass: 'mat-confirmation-dialog-responsive',
      data: {
        appointmentTypes: this.appointmentTypes.sort((a, b) => a.id > b.id ? 1 : -1),
        selectedDate: selectedTimeslot,
        selectedEndDate: endDate,
        intervals: this.intervals,
        practiceLocationId: this.selectedLocationId,
        practicePractitionerId: this.selectedPractitionerId
      } as NewAppointmentDialogData
    });
    dialogRef.afterClosed().subscribe(() => {
      if (this.selectedRow !== null) {
        this.dayView.selectRow(this.selectedRow);
      }
      this.loadDayViewAppointments();
    });
  }

  onViewChanged(newView: string): void {
    const d = dayjs(this.currentDate);
    const month = d.month() + 1;
    const day = d.date();
    const year = d.year();
    const url = this.actionHandler.buildCalendarRoute(newView, year, month, day);
    this.router.navigateByUrl(url);
  }

  onAppointmentMoved(request: AppointmentMoveRequest): void {
    const title = request.appointment.patient?.formalName
      || request.appointment.patient?.name
      || request.appointment.title
      || 'appointment';
    const formattedStart = dayjs(request.start).format('ddd, D MMM HH:mm');
    const confirmRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Move Appointment',
        message: `Move ${title} to ${formattedStart}?`,
      },
    });
    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) {
        request.revert();
        return;
      }
      const updated = calendarAppointmentToWriteModel(request.appointment, {
        start: request.start,
        end: request.end,
      });
      this.dataProvider.updateAppointment(updated).subscribe({
        next: () => this.calendarStore.refreshAppointments(),
        error: () => {
          request.revert();
          this.calendarStore.refreshAppointments();
        },
      });
    });
  }

  onFullCalendarAppointmentClicked(appointment: CalendarAppointment): void {
    const dialogRef = this.dialog.open(AppointmentDetailDialogComponent, {
      viewContainerRef: this.vcr,
      panelClass: 'mat-confirmation-dialog-responsive',
      data: {
        appointmentTypes: this.appointmentTypes.sort((a, b) => a.id > b.id ? 1 : -1),
        appointment
      } as AppointmentDetailDialogData
    });
    dialogRef.afterClosed().subscribe(() => {
      this.calendarStore.refreshAppointments();
    });
  }

  onHelperEventClicked(event: { start: Date; end: Date }): void {
    if (!this.appointmentTypes?.length) {
      this.fullCalendarView?.removeHelperEvent();
      return;
    }
    const dialogRef = this.dialog.open(NewAppointmentComponent, {
      disableClose: true,
      viewContainerRef: this.vcr,
      panelClass: 'mat-confirmation-dialog-responsive',
      data: {
        appointmentTypes: this.appointmentTypes.sort((a, b) => a.id > b.id ? 1 : -1),
        selectedDate: event.start,
        selectedEndDate: event.end,
        intervals: this.intervals,
        practiceLocationId: this.selectedLocationId,
        practicePractitionerId: this.selectedPractitionerId
      } as NewAppointmentDialogData
    });
    dialogRef.afterClosed().subscribe(() => {
      this.fullCalendarView?.removeHelperEvent();
      this.calendarStore.refreshAppointments();
    });
  }

  onFilterChanged(): void {
    if (this.view === 'daysheet') {
      this.loadDayViewAppointments();
    } else if (this.fullCalendarStartDate && this.fullCalendarEndDate) {
      this.calendarStore.loadAppointments(
        this.fullCalendarStartDate,
        this.fullCalendarEndDate,
        this.selectedLocationId,
        this.selectedPractitionerId
      );
    }
  }

  // --- DayView output handlers ---

  onDayViewDeleteRequested(appointment: CalendarAppointment): void {
    this.dataProvider.deleteAppointment(appointment.id).subscribe(() => {
      this.loadDayViewAppointments();
    });
  }

  onDayViewEditRequested(appointment: CalendarAppointment): void {
    const dialogRef = this.dialog.open(EditAppointmentComponent, {
      disableClose: true,
      viewContainerRef: this.vcr,
      panelClass: 'mat-confirmation-dialog-responsive',
      data: {
        appointmentTypes: this.appointmentTypes.sort((a, b) => a.id > b.id ? 1 : -1),
        appointment
      }
    });
    dialogRef.afterClosed().subscribe(() => {
      this.loadDayViewAppointments();
    });
  }

  onDayViewAddRequested(event: { timeslot: Date }): void {
    const defaultDuration = this.dataProvider.practiceSettings()?.defaultAppointmentDurationMinutes ?? 30;
    const endDate = new Date(event.timeslot.getTime() + defaultDuration * 60 * 1000);
    const dialogRef = this.dialog.open(NewAppointmentComponent, {
      disableClose: true,
      viewContainerRef: this.vcr,
      panelClass: 'mat-confirmation-dialog-responsive',
      data: {
        appointmentTypes: this.appointmentTypes.sort((a, b) => a.id > b.id ? 1 : -1),
        selectedDate: event.timeslot,
        selectedEndDate: endDate,
        intervals: this.intervals,
        practiceLocationId: this.selectedLocationId,
        practicePractitionerId: this.selectedPractitionerId
      } as NewAppointmentDialogData
    });
    dialogRef.afterClosed().subscribe(() => {
      if (this.selectedRow !== null) {
        this.dayView?.selectRow(this.selectedRow);
      }
      this.loadDayViewAppointments();
    });
  }

  onDayViewViewPatient(patient: Patient | QueryPatient): void {
    this.actionHandler.onViewPatientRequested(patient);
  }

  onDayViewCreatePatient(appointment: CalendarAppointment): void {
    this.actionHandler.onCreatePatientRequested(appointment).subscribe(patient => {
      if (patient) {
        const updated = calendarAppointmentToWriteModel(appointment, {patientId: patient.id});
        this.dataProvider.updateAppointment(updated).subscribe(() => {
          this.loadDayViewAppointments();
        });
      }
    });
  }

  onDayViewSendMessage(patient: Patient | QueryPatient): void {
    this.actionHandler.onSendMessageRequested?.(patient);
  }

  onDayViewSendEmail(patient: Patient | QueryPatient): void {
    this.actionHandler.onSendEmailRequested?.(patient);
  }

  // --- FullcalendarView output handler ---

  onDateRangeChanged(event: { startDate: string; endDate: string }): void {
    this.fullCalendarStartDate = event.startDate;
    this.fullCalendarEndDate = event.endDate;
    if (!this.initialized()) {
      return;
    }
    this.calendarStore.loadAppointments(
      event.startDate,
      event.endDate,
      this.selectedLocationId,
      this.selectedPractitionerId
    );
  }

  // --- Data loading for DayView ---

  loadDayViewAppointments(): void {
    if (!this.currentDate || !this.intervals?.length) {
      return;
    }
    this.dayViewSubscription?.unsubscribe();
    this.dayViewSubscription = this.dataProvider.loadAppointmentsByDay(
      this.currentDate, this.selectedLocationId, this.selectedPractitionerId
    ).subscribe(appointments => {
      this.dayViewAppointments.set(appointments);
    });
  }

  // --- WebSocket management ---

  private setupWebSocket(): void {
    const channelNames = this.dataProvider.getEventChannelNames();
    this.wsCallback = () => this.onWebSocketEvent();
    channelNames.forEach(channelName => {
      this.pushService.subscribe(channelName, Events.AppointmentAppointmentChanged, this.wsCallback);
      this.pushService.subscribe(channelName, Events.AppointmentAppointmentFlagsChanged, this.wsCallback);
    });
    this.subscribedChannelNames = channelNames;
  }

  private teardownWebSocket(): void {
    if (this.wsCallback) {
      this.subscribedChannelNames.forEach(channelName => {
        this.pushService.unsubscribe(channelName, Events.AppointmentAppointmentChanged, this.wsCallback);
        this.pushService.unsubscribe(channelName, Events.AppointmentAppointmentFlagsChanged, this.wsCallback);
      });
      this.wsCallback = null;
      this.subscribedChannelNames = [];
    }
  }

  private onWebSocketEvent(): void {
    if (this.view === 'daysheet') {
      this.loadDayViewAppointments();
    } else {
      this.calendarStore.refreshAppointments();
    }
  }

  private setupIntervals() {
    const startTime = new Date(this.currentDate);
    startTime.setHours(START_HOUR, 0, 0);
    const endTime = new Date(this.currentDate);
    endTime.setHours(END_HOUR, 0, 0);
    const intervals: Date[] = [];
    while (startTime <= endTime) {
      intervals.push(new Date(startTime));
      startTime.setMinutes(startTime.getMinutes() + TIMESLOT_MINS);
    }
    this.intervals = intervals;
  }
}
